import React, { useCallback, useEffect, useRef, useState } from 'react';
import LoadingLottie from '@/components/LoadingLottie';
import { HighZIndex } from '@/constant/z-index';
import {
  isApprovalDocOpening,
  openApprovalDocInApp,
} from '@/utils/approval/sendApprovalDocToApp';
import { isRnHost } from '@/utils/approval/isInApp';

type OpenOptions = {
  actionType?: 'MOVE_TAB' | 'MODAL';
  fallback?: 'legacy' | 'viewer';
};

/**
 * 앱 웹뷰에서 결재 문서 열기 + 판별 대기 중 로딩 표시.
 *
 * 양식 코드를 모르는 진입(알림·최근 본 문서)은 단건 조회를 한 번 거쳐야 어느 화면을
 * 열지 정해진다. 그 동안 화면에 아무 변화가 없으면 탭이 먹히지 않은 것처럼 보인다 —
 * 대기 중 재탭은 무시되기까지 해서 더 그렇다.
 *
 * 대기 상한은 API 타임아웃(8초)이다. 조회가 실패해도 폴백 URL 로 전송하고 끝나므로
 * 로딩이 남지 않는다.
 */
const useOpenApprovalDocInApp = () => {
  const [isOpening, setIsOpening] = useState(false);
  // 화면 전환 중 언마운트된 뒤 응답이 도착해 상태를 건드리는 것 방지
  const isMountedRef = useRef(true);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );

  /**
   * @returns 이 탭을 받아 문서를 여는 중이면 true. false 면 열지 않았다는 뜻으로,
   * 앱 웹뷰가 아니거나 직전 탭을 아직 처리 중인 경우다. 호출부는 false 를 받으면
   * 그 탭에 딸린 후처리(알림 읽음 처리 등)도 함께 건너뛸 것.
   */
  const openDoc = useCallback((docId: string, options?: OpenOptions) => {
    // 전송 채널이 앱 웹뷰뿐이라 웹에서는 조회해봐야 열리지 않는다.
    // 게이트가 없으면 웹에서 로딩만 떴다 사라진다(웹 분기는 호출부 각자의 몫).
    if (!isRnHost()) return false;
    // 유틸이 무시할 탭이면 로딩도 띄우지 않는다 — 띄운 쪽과 내리는 쪽이 같아야 함
    if (isApprovalDocOpening()) return false;

    setIsOpening(true);
    void openApprovalDocInApp(docId, options).finally(() => {
      if (isMountedRef.current) setIsOpening(false);
    });

    return true;
  }, []);

  /*
   * LoadingLottie 는 자체적으로 absolute + 100vw/100vh 라, 팝오버·모달 안에서 렌더되면
   * 그 안에 갇힌다. fixed 래퍼로 감싸 화면 전체를 덮는다.
   * 덮는 김에 대기 중 다른 항목 탭도 막힌다 — 어차피 유틸이 무시할 탭이다.
   */
  const loadingOverlay = isOpening ? (
    <div style={{ position: 'fixed', inset: 0, zIndex: HighZIndex }}>
      <LoadingLottie opacity={1} loop />
    </div>
  ) : null;

  return { openDoc, isOpening, loadingOverlay };
};

export default useOpenApprovalDocInApp;
